import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import whiteLogo from '../assets/aurora-wealth-reserves-logo-white.png';

const Sidebar = ({ isOpen, setIsOpen, isDesktop = false }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedMenuItem, setExpandedMenuItem] = useState(null);

  const navigationItems = [
    {
      section: 'Overview',
      items: [
        {
          name: 'Dashboard',
          path: '/dashboard',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
          )
        },
        {
          name: 'Account Summary',
          path: '/accounts',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-4 0H3m2-16h14" />
            </svg>
          )
        }
      ]
    },
    {
      section: 'Banking',
      items: [
        {
          name: 'Send & Transfer',
          path: '/transfer',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          ),
          subItems: [
            { name: 'Between My Accounts', path: '/transfer' },
            { name: 'Wire Transfer', path: '/wire-transfer' },
            { name: 'Zelle®', path: '/zelle' }
          ]
        },
        {
          name: 'Check Deposit',
          path: '/deposit',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 7h18M5 11h7m-7 4h4m-4 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          )
        },
        {
          name: 'Bill Pay',
          path: '/bill-pay',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )
        }
      ]
    },
    {
      section: 'Investments',
      items: [
        {
          name: 'Crypto Portfolio',
          path: '/crypto-trading',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        }
      ]
    },
    {
      section: 'Services',
      items: [
        {
          name: 'Cards & ATM',
          path: '/cards',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          )
        },
        {
          name: 'Loans',
          path: '/loans',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )
        },
        {
          name: 'Insurance',
          path: '/insurance',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          )
        },
        {
          name: 'Customer Support',
          path: '/support',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )
        }
      ]
    }
  ];

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Mobile Overlay - Only show on mobile when sidebar is open */}
      <AnimatePresence>
        {isOpen && !isDesktop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        initial={isDesktop ? { x: 0 } : { x: -320 }}
        animate={isDesktop ? { x: 0 } : { x: isOpen ? 0 : -320 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={`${
          isDesktop 
            ? 'h-full w-full bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 shadow-2xl' 
            : 'fixed left-0 top-0 h-full w-80 z-50 lg:hidden bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 shadow-2xl'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-blue-700">
          <img 
            src={whiteLogo} 
            alt="Aurora Wealth Reserves" 
            className="h-12 w-auto" 
          />
          {!isDesktop && (
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-blue-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* User Profile */}
        <div className="p-6 border-b border-blue-700">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="text-white font-semibold">{user?.name || 'User'}</p>
              <p className="text-blue-200 text-sm">Premium Banking</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-4">
          {navigationItems.map((section) => (
            <div key={section.section} className="mb-6">
              <button
                onClick={() => toggleSection(section.section)}
                className="w-full flex items-center justify-between text-left text-blue-200 font-semibold text-sm uppercase tracking-wider mb-3 hover:text-white transition-colors"
              >
                {section.section}
                <svg
                  className={`w-4 h-4 transition-transform ${
                    expandedSection === section.section ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <AnimatePresence>
                {(expandedSection === section.section || expandedSection === null) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-1"
                  >
                    {section.items.map((item) => (
                      <div key={item.path}>
                        {/* Main menu item */}
                        <div className="flex items-center">
                          <NavLink
                            to={item.subItems ? '#' : item.path}
                            className={({ isActive }) =>
                              `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 flex-1 ${
                                isActive && !item.subItems
                                  ? 'bg-blue-600 text-white shadow-lg'
                                  : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                              }`
                            }
                            onClick={(e) => {
                              if (item.subItems) {
                                e.preventDefault();
                                setExpandedMenuItem(expandedMenuItem === item.name ? null : item.name);
                              } else {
                                !isDesktop && setIsOpen(false);
                              }
                            }}
                          >
                            {item.icon}
                            <span className="font-medium">{item.name}</span>
                          </NavLink>
                          
                          {/* Expand arrow for items with sub-items */}
                          {item.subItems && (
                            <button
                              onClick={() => setExpandedMenuItem(expandedMenuItem === item.name ? null : item.name)}
                              className="p-2 text-blue-100 hover:text-white transition-colors"
                            >
                              <svg
                                className={`w-4 h-4 transition-transform ${
                                  expandedMenuItem === item.name ? 'rotate-90' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          )}
                        </div>

                        {/* Sub-items */}
                        {item.subItems && (
                          <AnimatePresence>
                            {expandedMenuItem === item.name && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="ml-4 mt-2 space-y-1"
                              >
                                {item.subItems.map((subItem) => (
                                  <NavLink
                                    key={subItem.path}
                                    to={subItem.path}
                                    className={({ isActive }) =>
                                      `flex items-center space-x-3 px-4 py-2 rounded-lg transition-all duration-200 text-sm ${
                                        isActive
                                          ? 'bg-blue-500 text-white shadow-lg'
                                          : 'text-blue-200 hover:bg-blue-600 hover:text-white'
                                      }`
                                    }
                                    onClick={() => !isDesktop && setIsOpen(false)}
                                  >
                                    <div className="w-2 h-2 bg-current rounded-full opacity-60"></div>
                                    <span className="font-medium">{subItem.name}</span>
                                  </NavLink>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-blue-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 text-blue-100 hover:bg-blue-700 hover:text-white rounded-lg transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar; 